import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppStore } from "lib/store/useAppStore.js";
import { getProject } from "lib/api/getProject.js";
import { getProjectUd1000ReportData } from "lib/api/getProjectUd1000ReportData.js";
import { useQueryParams } from "lib/hooks/useQureyParmas.jsx";
import { FILE_SORT_PREF_EVENT, getFileSortPref } from "lib/utils/fileSortpref";
import { parseFileDetailFiltersFromSearchParams } from "lib/utils/fileDetailFilters";
import { getProjectReportListScope } from "lib/utils/projectReportListScope";
import { useTranslation } from "react-i18next";
import GoBackButton from "components/project/GoBackButton.jsx";
import ProjectFileListSection from "./components/ProjectFileListSection.jsx";
import ProjectDetailControlPanel from "./components/ProjectDetailControlPanel.jsx";
import ProjectOptionalTitle from "./components/ProjectOptionalTitle.jsx";
import { PROJECT_ROADMAP_QUERY_KEYS } from "./components/ProjectRoadmapPanel.jsx";
import Spinner from "components/Spinner.jsx";
import HelpTip from "components/HelpTip.jsx";

function normalizeComparableId(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

export default function ProjectDetailPage() {
  // ____________ store & router ____________
  const status = useAppStore((s) => s.status);
  const bootstrapping = useAppStore((s) => s.bootstrapping);
  const user = useAppStore((s) => s.user);
  const fetchProjectTree = useAppStore((s) => s.fetchProjectTree);
  const showAlert = useAppStore((s) => s.showAlert);

  // ✅ store에 refresh 액션이 있다면(추천 구조), 401 시 재시도에 사용
  const refresh = useAppStore((s) => s.refresh); // 없으면 store에 추가 필요

  // ____________ query params ____________
  const { project_id } = useQueryParams();

  // ✅ project_id가 없을 수 있으니 fallback
  const resolvedProjectId = project_id || user?.last_selected_project || null;

  // URL 쿼리와 동기화 (page/limit)
  const [sp, setSp] = useSearchParams();
  const previewScopeJson = JSON.stringify(getProjectReportListScope(sp));
  const previewScopeKey = `${resolvedProjectId}:${previewScopeJson}`;
  const currentPreviewScope = useRef(previewScopeKey);
  currentPreviewScope.current = previewScopeKey;
  const previewRequest = useRef(0);
  const page = useMemo(() => Number(sp.get("page") ?? 1), [sp]);
  const limit = useMemo(() => Number(sp.get("limit") ?? 20), [sp]);
  const category = sp.get("category");
  const index = sp.get("index");
  const value = sp.get("value");
  const treeBuildingName = sp.get(PROJECT_ROADMAP_QUERY_KEYS.buildingName);
  const treeFloor = sp.get(PROJECT_ROADMAP_QUERY_KEYS.floor);
  const treeLocation = sp.get(PROJECT_ROADMAP_QUERY_KEYS.location);
  const treeFacilityType = sp.get(PROJECT_ROADMAP_QUERY_KEYS.facilityType);
  const treeFacilityName = sp.get(PROJECT_ROADMAP_QUERY_KEYS.facilityName);
  const searchCategory = sp.get("searchCategory");
  const searchKeyword = sp.get("searchKeyword") ?? "";
  const organizationScope = sp.get("organizationScope");
  const detailFilters = useMemo(
    () => parseFileDetailFiltersFromSearchParams(sp),
    [sp],
  );

  const { t, i18n } = useTranslation();

  // ____________ 상태 (project 전용) ____________
  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [projectReportPreviewFiles, setProjectReportPreviewFiles] = useState(
    [],
  );
  const [
    projectReportPreviewLoadedProjectId,
    setProjectReportPreviewLoadedProjectId,
  ] = useState(null);
  const [
    projectReportPreviewFilesLoading,
    setProjectReportPreviewFilesLoading,
  ] = useState(false);
  const [projectRefreshVersion, setProjectRefreshVersion] = useState(0);
  const [metricsSettingsScope, setMetricsSettingsScope] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [form, setForm] = useState({
    id: "",
    user_id: "",
    project_name: "",
    contract_name: "",
    period_start: "",
    period_end: "",
    contractor: "",
    purpose: "",
    participants: "",
    analysis_result: "",
  });
  const [prevData, setPrevData] = useState(null);
  const isProjectReportPreviewLoaded =
    Boolean(resolvedProjectId) &&
    projectReportPreviewLoadedProjectId === previewScopeKey;

  // ✅ 전역 부팅이 끝나기 전엔 아무것도 하지 않음
  const isReady = status !== "unknown" && !bootstrapping;
  const isChildOrganizationProject = useMemo(() => {
    if (organizationScope === "child") return true;

    const projectOrganizationId = normalizeComparableId(
      project?.organization_id ?? project?.organizationId,
    );
    const userOrganizationId = normalizeComparableId(user?.organization_id);

    return Boolean(
      projectOrganizationId &&
      userOrganizationId &&
      projectOrganizationId !== userOrganizationId,
    );
  }, [
    organizationScope,
    project?.organization_id,
    project?.organizationId,
    user?.organization_id,
  ]);

  const fetchProjectRecord = useCallback(async () => {
    if (!user?.id || !resolvedProjectId) {
      return {
        success: false,
        skipped: true,
        project: null,
        errorMessage: null,
      };
    }

    try {
      let result = await getProject(resolvedProjectId);

      const maybeUnauthorized =
        result?.status === 401 ||
        result?.code === 401 ||
        result?.message?.toLowerCase?.().includes("unauthorized");

      if (!result?.success && refresh && maybeUnauthorized) {
        await refresh();
        result = await getProject(resolvedProjectId);
      }

      if (!result?.success) {
        return {
          success: false,
          skipped: false,
          project: null,
          errorMessage: result?.message || "프로젝트를 불러오지 못했습니다.",
        };
      }

      return {
        success: true,
        skipped: false,
        project: result.project ?? null,
        errorMessage: null,
      };
    } catch (error) {
      const statusCode = error?.response?.status || error?.status;

      if (statusCode === 401 && refresh) {
        try {
          await refresh();
          const retriedResult = await getProject(resolvedProjectId);

          if (retriedResult?.success) {
            return {
              success: true,
              skipped: false,
              project: retriedResult.project ?? null,
              errorMessage: null,
            };
          }

          return {
            success: false,
            skipped: false,
            project: null,
            errorMessage: retriedResult?.message || "프로젝트를 불러오지 못했습니다.",
          };
        } catch (retryError) {
          console.error("[ProjectDetail] getProject retry failed:", retryError);
          return {
            success: false,
            skipped: false,
            project: null,
            errorMessage:
              "로그인 상태를 확인할 수 없습니다. 다시 로그인해 주세요.",
          };
        }
      }

      console.error("[ProjectDetail] getProject ?ㅽ뙣:", error);

      return {
        success: false,
        skipped: false,
        project: null,
        errorMessage: "프로젝트를 불러오지 못했습니다. 다시 시도해 주세요.",
      };
    }
  }, [refresh, resolvedProjectId, user?.id]);

  const loadProject = useCallback(
    async ({ withLoading = false } = {}) => {
      if (withLoading) {
        setProjectLoading(true);
      }

      setProjectError(null);

      try {
        const result = await fetchProjectRecord();

        if (result?.skipped) {
          return result;
        }

        if (!result?.success) {
          setProject(null);
          setProjectError(result?.errorMessage || "프로젝트를 불러오지 못했습니다.");
          return result;
        }

        setProject(result.project);
        return result;
      } finally {
        if (withLoading) {
          setProjectLoading(false);
        }
      }
    },
    [fetchProjectRecord],
  );

  const loadProjectReportPreviewFiles = useCallback(async () => {
    if (!resolvedProjectId) return;
    const requestId = ++previewRequest.current;
    const isCurrent = () => requestId === previewRequest.current && currentPreviewScope.current === previewScopeKey;
    setPreviewError("");
    setProjectReportPreviewFilesLoading(true);

    try {
      const { sortKey, sortDir } = getFileSortPref(user?.id);
      const result = await getProjectUd1000ReportData(resolvedProjectId, {
        sortKey,
        sortDir,
        listScope: JSON.parse(previewScopeJson),
      });
      if (!isCurrent()) return;

      if (!result?.success) {
        showAlert?.({
          text:
            result?.message ||
            t("projectDetail.controlPanel.sections.reportSummary.loadFailed", {
              defaultValue: "Failed to load report preview data.",
            }),
          success: false,
          autoClose: true,
        });
        setProjectReportPreviewFiles([]);
        setProjectReportPreviewLoadedProjectId(null);
        setPreviewError(result?.message || "지표를 불러오지 못했습니다.");
        return;
      }

      setProjectReportPreviewFiles(
        Array.isArray(result.files) ? result.files : [],
      );
      setProjectReportPreviewLoadedProjectId(previewScopeKey);
    } catch (error) {
      if (!isCurrent()) return;
      setPreviewError("지표를 불러오지 못했습니다.");
      console.error(
        "[ProjectDetail] failed to load project report preview files:",
        error,
      );
      showAlert?.({
        text: t(
          "projectDetail.controlPanel.sections.reportSummary.loadFailed",
          {
            defaultValue: "Failed to load report preview data.",
          },
        ),
        success: false,
        autoClose: true,
      });
      setProjectReportPreviewFiles([]);
      setProjectReportPreviewLoadedProjectId(null);
    } finally {
      if (isCurrent()) setProjectReportPreviewFilesLoading(false);
    }
  }, [resolvedProjectId, previewScopeKey, previewScopeJson, showAlert, t, user?.id]);

  const ensureProjectReportPreviewFiles = useCallback(async () => {
    if (
      !resolvedProjectId ||
      status !== "authenticated" ||
      previewError ||
      isProjectReportPreviewLoaded ||
      projectReportPreviewFilesLoading
    ) {
      return;
    }

    await loadProjectReportPreviewFiles();
  }, [
    isProjectReportPreviewLoaded,
    loadProjectReportPreviewFiles,
    projectReportPreviewFilesLoading,
    resolvedProjectId,
    status,
    previewError,
  ]);

  const refreshProjectDetailData = useCallback(
    async ({ refreshTree = false, withLoading = false } = {}) => {
      const tasks = [loadProject({ withLoading })];

      if (isProjectReportPreviewLoaded) {
        tasks.push(loadProjectReportPreviewFiles());
      }

      if (refreshTree && user?.id && resolvedProjectId) {
        tasks.unshift(fetchProjectTree?.(user.id, resolvedProjectId));
      }

      await Promise.allSettled(tasks.filter(Boolean));
      setProjectRefreshVersion((previousVersion) => previousVersion + 1);
    },
    [
      fetchProjectTree,
      isProjectReportPreviewLoaded,
      loadProject,
      loadProjectReportPreviewFiles,
      resolvedProjectId,
      user?.id,
    ],
  );

  /* ───────────────────────────────────────────────
   STEP : project 정보 로드 (401이면 refresh 후 1회 재시도)
   ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!isReady) return;

    if (status !== "authenticated") {
      setProject(null);
      setProjectError(null);
      setProjectReportPreviewFiles([]);
      setProjectReportPreviewLoadedProjectId(null);
      setProjectRefreshVersion(0);
      return;
    }

    if (!user?.id || !resolvedProjectId) return;

    let ignore = false;

    (async () => {
      setProjectLoading(true);
      setProjectError(null);

      try {
        const result = await fetchProjectRecord();
        if (ignore) return;

        if (result?.skipped) {
          setProject(null);
          return;
        }

        if (!result?.success) {
          setProject(null);
          setProjectError(
            result?.errorMessage ||
              t("projectDetail.errorFetchFailed", {
                defaultValue: "Failed to load project information.",
              }),
          );
          return;
        }

        setProject(result.project);
      } catch (error) {
        if (ignore) return;

        console.error("[ProjectDetail] failed to load project:", error);
        setProjectError(
          t("projectDetail.errorServerRequest", {
            defaultValue: "An error occurred while requesting the server.",
          }),
        );
      } finally {
        if (!ignore) setProjectLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [fetchProjectRecord, isReady, resolvedProjectId, status, t, user?.id]);

  useLayoutEffect(() => {
    previewRequest.current += 1;
    setProjectReportPreviewFiles([]);
    setProjectReportPreviewLoadedProjectId(null);
    setProjectReportPreviewFilesLoading(false);
    setPreviewError("");
  }, [previewScopeKey]);

  useEffect(() => () => { previewRequest.current += 1; }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !user?.id || !resolvedProjectId) {
      return undefined;
    }

    const handleFileSortPrefChange = async (event) => {
      const changedUserId = event?.detail?.userId;
      if (changedUserId && changedUserId !== user.id) return;

      if (isProjectReportPreviewLoaded) {
        await loadProjectReportPreviewFiles();
      }
    };

    window.addEventListener(FILE_SORT_PREF_EVENT, handleFileSortPrefChange);

    return () => {
      window.removeEventListener(
        FILE_SORT_PREF_EVENT,
        handleFileSortPrefChange,
      );
    };
  }, [
    isProjectReportPreviewLoaded,
    loadProjectReportPreviewFiles,
    resolvedProjectId,
    user?.id,
  ]);

  // ✅ 부팅 중이면 스피너 (전역에서도 하지만, 이 페이지 단독 마운트 대비)
  if (status === "unknown" || bootstrapping) {
    return (
      <section className="projectDetail content">
        <div className="full-loader">
          <Spinner />
        </div>
      </section>
    );
  }

  // 공용 props (자식들에게 전달)
  const props = {
    data: project,
    setdata: setProject,
    form,
    setForm,
    prevData,
    setPrevData,
    isReadOnly,
    setIsReadOnly,
    project_id: resolvedProjectId,
    project_name: project?.project_name,
    user_id: user?.id,
    type: "project",
    refetch: () => refreshProjectDetailData({ refreshTree: false }),

    projectLoading,
    projectError,
    t,
  };

  return (
    <section className="projectDetail content">
      <div className="editorFormTitleBox helpTitleRow">
        {/* {!isChildOrganizationProject && (
          <GoBackButton
            t={t}
            category={category}
            value={value}
            index={index}
            treeBuildingName={treeBuildingName}
            treeFloor={treeFloor}
            treeLocation={treeLocation}
            treeFacilityType={treeFacilityType}
            treeFacilityName={treeFacilityName}
          />
        )} */}
        <ProjectOptionalTitle
          project_id={resolvedProjectId}
          project_name={project?.project_name}
          onOpenMetrics={() => setMetricsSettingsScope(previewScopeKey)}
        />
        {/* <HelpTip tipKey={"GET_PROJECT"} /> */}
      </div>

      <ProjectDetailControlPanel
        settingsOpen={metricsSettingsScope === previewScopeKey}
        onSettingsClose={() => setMetricsSettingsScope(null)}
        projectId={resolvedProjectId}
        listScope={JSON.parse(previewScopeJson)}
        error={previewError}
        loaded={isProjectReportPreviewLoaded}
        onRetry={loadProjectReportPreviewFiles}
        project={project}
        editorProps={props}
        t={t}
        i18n={i18n}
        reportFiles={isProjectReportPreviewLoaded ? projectReportPreviewFiles : []}
        reportFilesLoading={projectReportPreviewFilesLoading}
        refreshVersion={projectRefreshVersion}
        onReportPreviewRequest={ensureProjectReportPreviewFiles}
      />

      <ProjectFileListSection
        page={page}
        index={index}
        category={category}
        value={value}
        treeBuildingName={treeBuildingName}
        treeFloor={treeFloor}
        treeLocation={treeLocation}
        treeFacilityType={treeFacilityType}
        treeFacilityName={treeFacilityName}
        searchCategory={searchCategory}
        searchKeyword={searchKeyword}
        limit={limit}
        user_id={user?.id}
        project_id={resolvedProjectId}
        project_name={project?.project_name}
        fetchProjectTree={fetchProjectTree}
        sp={sp}
        setSp={setSp}
        t={t}
        i18n={i18n}
        detailFilters={detailFilters}
        onProjectMutation={() =>
          refreshProjectDetailData({ refreshTree: false })
        }
      />
    </section>
  );
}
